import React from "react";
import styled from "styled-components";
import deprecatedTheme from "../../../../styling/deprecated_theme";
import FormControl from "../../inputs/form_control";
import Radio, { RadioGroup, RadioGroupProps, ALIGN } from "../../inputs/radio";
import { Radio as BaseRadio, RadioGroupOverrides } from "baseui/radio";
import PromptWithSecondaryText from "./shared_components/prompt_with_secondary_text";
import { Overrides } from "baseui/overrides";
import nextId from "react-id-generator";
import TextAreaQuestionWrapper, { TextareaWrapperProps } from "./textarea_question_wrapper";
import { useConstructAriaLabel } from "./use_construct_aria_label";
import MultiSelectQuestionWrapper, { MultiSelectOption } from "./multiselect_question_wrapper";

const LIKERT_RADIO_NAMESPACE = "likert-radio-group";

export interface FollowUpLowQuestionProps {
  type: string;
  options: MultiSelectOption[];
  optional: boolean;
  onChange: (newAnswer: MultiSelectOption) => void;
  setAbleToSubmit: (ableToSubmit: boolean) => void;
  showErrors: boolean;
}

export interface LikertQuestionWrapperProps extends RadioGroupProps {
  prompt: string;
  description?: string;
  optional?: boolean;
  min?: number;
  minLabel?: string;
  max?: number;
  maxLabel?: string;
  displayValues?: { [key: number]: string | number };
  errorMessage?: string;
  followUpPrompt?: string; // deprecated
  followUpResponse?: string; // deprecated
  followUpLowScore?: boolean;
  lowScoreThreshold?: number;
  followUpLowPrompt?: string;
  followUpLowResponse?: string;
  followUpHighScore?: boolean;
  highScoreThreshold?: number;
  followUpHighPrompt?: string;
  followUpHighResponse?: string;
  followUpCurrentCharacterCount?: TextareaWrapperProps["currentCharacterCount"];
  followUpMaxCharacterCount?: TextareaWrapperProps["maxCharacterCount"];
  onChangeFollowUp?: TextareaWrapperProps["onChange"];
  followUpLowQuestion?: FollowUpLowQuestionProps;
  followUpLowDescription?: string;
  followUpErrorMessage?: string;
  followUpIsOptional?: boolean;
  dataTest?: string;
}

const StyledRadioLabel = styled.div`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
`;
const StyledTextAreaContainer = styled.div`
  max-width: 700px;
  margin-top: ${({ theme }) => theme.sizing.scale800};
`;

const likertRadioGroupOverrides: Overrides<RadioGroupOverrides> = {
  RadioGroupRoot: {
    style: ({ $theme }) => ({
      display: "flex",
      alignItems: "flex-start",
      gap: $theme.sizing.scale800,
      maxWidth: "800px",
    }),
  },
};

const likertRadioOverrides: Overrides<BaseRadio> = {
  Root: {
    style: ({ $theme }) => ({
      display: "flex",
      flexDirection: "column",
      gap: $theme.sizing.scale300,
      marginTop: "0px",
      marginRight: "0px",
    }),
  },
  Label: {
    style: {
      maxWidth: "124px",
      paddingLeft: "0px",
      textAlign: "center",
    },
  },
};

const likertRadioGroupOverridesMobile: Overrides<BaseRadio> = {};
const likertRadioOverridesMobile: Overrides<BaseRadio> = {};

const LikertQuestionWrapper = ({
  prompt,
  description,
  optional = false,
  min = 1,
  minLabel = "Low",
  max = 7,
  maxLabel = "High",
  displayValues,
  errorMessage,

  followUpPrompt, // deprecated
  followUpResponse, // deprecated

  followUpLowScore = false,
  lowScoreThreshold = 4,
  followUpLowPrompt,
  followUpLowResponse,
  followUpLowQuestion,
  followUpLowDescription,

  followUpHighScore = false,
  highScoreThreshold = 5,
  followUpHighPrompt,
  followUpHighResponse,

  followUpIsOptional,
  followUpCurrentCharacterCount = 0,
  followUpMaxCharacterCount = 1200,
  onChangeFollowUp,
  followUpErrorMessage,
  dataTest = "likert-question-wrapper",
  ...radioGroupProps
}: LikertQuestionWrapperProps) => {
  const { constructAriaLabel } = useConstructAriaLabel();
  const secondaryText = description
    ? description
    : displayValues
      ? ""
      : `${min}=${minLabel} ${max}=${maxLabel}`;

  // DEPRECATION: avoid using followUpPrompt in favor of followUpLowPrompt
  //              avoid using followUpResponse in favor of followUpLowResponse
  // this aliasing is here to support pre-existing likert questions
  followUpLowPrompt = followUpLowPrompt || followUpPrompt;
  followUpLowResponse = followUpLowResponse || followUpResponse;

  const inputId = nextId(LIKERT_RADIO_NAMESPACE);
  const value = radioGroupProps.value ? parseInt(radioGroupProps.value) : undefined;

  // create a new list of display values that includes all sequential numbers between the
  // specified min and max, regardless of if a display value is provided for that number
  const radioValues: LikertQuestionWrapperProps["displayValues"] = {};
  for (let i = min; i <= max; i++) {
    radioValues[i] = displayValues && displayValues[i] ? displayValues[i] : "";
  }

  const followUpLow = value !== undefined && value <= lowScoreThreshold && followUpLowScore;
  const followUpHigh = value !== undefined && value >= highScoreThreshold && followUpHighScore;

  const followUpQuestionPrompt = followUpLow
    ? followUpLowPrompt
    : followUpHigh
      ? followUpHighPrompt
      : "Can you share more?";

  const followUpQuestionResponse = followUpLow
    ? followUpLowResponse
    : followUpHigh
      ? followUpHighResponse
      : ""; // setting this to undefined causes weird behavior with inputs that then think they are uncontrolled when they are not

  // check size of window to determine if we should show mobile view - the mobile view
  // layout is quite different from the desktop view and we cannot make it work through
  // the use of overrides so assigning this boolean upfront is the best way to go
  const mobileView = window.matchMedia(deprecatedTheme.deprecatedBreakpoints.small).matches;

  return (
    <div>
      <PromptWithSecondaryText
        supplementalQuestionPrompt={prompt}
        secondaryText={secondaryText}
        optional={optional}
        dataTest={dataTest}
      />
      <div>
        <FormControl htmlFor={inputId} error={errorMessage} dataTest={dataTest}>
          <RadioGroup
            name={prompt}
            id={inputId}
            aria-label={constructAriaLabel(prompt, description, secondaryText, optional)}
            align={mobileView ? ALIGN.vertical : ALIGN.horizontal}
            overrides={mobileView ? likertRadioGroupOverridesMobile : likertRadioGroupOverrides}
            error={!!errorMessage}
            {...radioGroupProps}
          >
            {Object.entries(radioValues).map(([k, v]) => (
              <Radio
                key={k}
                value={k}
                overrides={mobileView ? likertRadioOverridesMobile : likertRadioOverrides}
                dataTest={`${dataTest}-radio-${k}`}
                tab-index={0}
              >
                {mobileView ? (
                  <StyledRadioLabel>{k && v ? `${k}- ${v}` : `${k}`}</StyledRadioLabel>
                ) : (
                  <>
                    <div>{k}</div>
                    {k && (
                      <StyledRadioLabel data-test={`${dataTest}-radio-label-${k}`}>
                        {v}
                      </StyledRadioLabel>
                    )}
                  </>
                )}
              </Radio>
            ))}
          </RadioGroup>
        </FormControl>
      </div>
      {(followUpLow || followUpHigh) &&
        (followUpLowQuestion?.type === "multi_select" ? (
          <MultiSelectQuestionWrapper
            questionPrompt={followUpLowPrompt || ""}
            secondaryText={followUpLowDescription}
            optional={followUpLowQuestion.optional}
            options={followUpLowQuestion.options}
            showErrors={followUpLowQuestion.showErrors}
            onChange={followUpLowQuestion.onChange}
            setAbleToSubmit={followUpLowQuestion.setAbleToSubmit}
            dataTest={`${dataTest}-multi-select-follow-up-question`}
          ></MultiSelectQuestionWrapper>
        ) : (
          <StyledTextAreaContainer>
            <TextAreaQuestionWrapper
              optional={followUpIsOptional !== undefined ? followUpIsOptional : true}
              supplementalQuestionPrompt={followUpQuestionPrompt}
              placeholder={"Type your response here..."}
              value={followUpQuestionResponse ?? ""}
              currentCharacterCount={followUpCurrentCharacterCount}
              maxCharacterCount={followUpMaxCharacterCount}
              onChange={onChangeFollowUp}
              errorMessage={followUpErrorMessage}
              dataTest={`${dataTest}-textarea-follow-up-question`}
            />
          </StyledTextAreaContainer>
        ))}
    </div>
  );
};

export default LikertQuestionWrapper;
